/*
 * Poly2Tri Copyright (c) 2009-2014, Poly2Tri Contributors
 * http://code.google.com/p/poly2tri/
 *
 * poly2tri.js (JavaScript port) (c) 2009-2014, Poly2Tri Contributors
 * https://github.com/r3mi/poly2tri.js
 *
 * All rights reserved.
 *
 * Distributed under the 3-clause BSD License, see LICENSE.txt
 */

import { PointError } from './pointerror'
import { Point } from './point'
import { Triangle } from './triangle'
import { AdvancingFront, Node } from './advancingfront'
import { triangulate as sweepTriangulate } from './sweep'
import type { XY } from './xy'

const kAlpha = 0.3 // Initial triangle factor

/**
 * Represents a simple polygon's edge
 */
export class Edge {
	p: XY
	q: XY

	constructor(p1: XY, p2: XY) {
		if (p1.y > p2.y) {
			this.q = p1
			this.p = p2
		} else if (p1.y === p2.y) {
			if (p1.x > p2.x) {
				this.q = p1
				this.p = p2
			} else if (p1.x === p2.x) {
				throw new PointError('poly2tri Invalid Edge constructor: repeated points!', [p1])
			} else {
				this.p = p1
				this.q = p2
			}
		} else {
			this.p = p1
			this.q = p2
		}

		if (!this.q._p2t_edge_list) {
			this.q._p2t_edge_list = []
		}
		this.q._p2t_edge_list.push(this)
	}
}

/**
 * Basin structure for advancing front algorithm
 */
export class Basin {
	left_node: Node | null = null
	bottom_node: Node | null = null
	right_node: Node | null = null
	width = 0.0
	left_highest = false

	clear(): void {
		this.left_node = null
		this.bottom_node = null
		this.right_node = null
		this.width = 0.0
		this.left_highest = false
	}
}

/**
 * EdgeEvent structure
 */
export class EdgeEvent {
	constrained_edge: Edge | null = null
	right = false
}

/**
 * SweepContext constructor options
 */
export interface SweepContextOptions {
	cloneArrays?: boolean
}

/**
 * Constructor for the triangulation context.
 * It accepts a simple polyline (with non repeating points),
 * which defines the constrained edges.
 */
export class SweepContext {
	private triangles_: Triangle[] = []
	private map_: Triangle[] = []
	private points_: XY[]
	edge_list: Edge[] = []
	private pmin_: Point | null = null
	private pmax_: Point | null = null
	private front_: AdvancingFront | null = null
	private head_: Point | null = null
	private tail_: Point | null = null
	basin = new Basin()
	edge_event = new EdgeEvent()

	constructor(contour: XY[], options: SweepContextOptions = {}) {
		this.points_ = options.cloneArrays ? contour.slice(0) : contour
		this.initEdges(this.points_)
	}

	/**
	 * Add a hole to the constraints
	 */
	addHole(polyline: XY[]): this {
		this.initEdges(polyline)
		for (const point of polyline) {
			this.points_.push(point)
		}
		return this
	}

	/**
	 * For backward compatibility
	 */
	AddHole = this.addHole

	/**
	 * Add several holes to the constraints
	 */
	addHoles(holes: XY[][]): this {
		for (const hole of holes) {
			this.initEdges(hole)
		}
		this.points_ = this.points_.concat(...holes)
		return this
	}

	/**
	 * Add a Steiner point to the constraints
	 */
	addPoint(point: XY): this {
		this.points_.push(point)
		return this
	}

	/**
	 * For backward compatibility
	 */
	AddPoint = this.addPoint

	/**
	 * Add several Steiner points to the constraints
	 */
	addPoints(points: XY[]): this {
		this.points_ = this.points_.concat(points)
		return this
	}

	/**
	 * Triangulate the polygon with holes and Steiner points.
	 * Do this AFTER you've added the polyline, holes, and Steiner points
	 */
	triangulate(): this {
		sweepTriangulate(this)
		return this
	}

	/**
	 * Get the bounding box of the provided constraints (contour, holes and
	 * Steiner points). Warning : these values are not available if the triangulation
	 * has not been done yet.
	 */
	getBoundingBox(): { min: Point | null; max: Point | null } {
		return { min: this.pmin_, max: this.pmax_ }
	}

	/**
	 * Get result of triangulation.
	 * The output triangles have vertices which are references
	 * to the initial input points (not copies): any custom fields in the
	 * initial points can be retrieved in the output triangles.
	 */
	getTriangles(): Triangle[] {
		return this.triangles_
	}

	/**
	 * For backward compatibility
	 */
	GetTriangles = this.getTriangles

	// Private/Internal API

	front(): AdvancingFront | null {
		return this.front_
	}

	pointCount(): number {
		return this.points_.length
	}

	head(): Point | null {
		return this.head_
	}

	setHead(p1: Point): void {
		this.head_ = p1
	}

	tail(): Point | null {
		return this.tail_
	}

	setTail(p1: Point): void {
		this.tail_ = p1
	}

	getMap(): Triangle[] {
		return this.map_
	}

	initTriangulation(): void {
		let xmax = this.points_[0].x
		let xmin = this.points_[0].x
		let ymax = this.points_[0].y
		let ymin = this.points_[0].y

		// Calculate bounds
		for (let i = 1; i < this.points_.length; i++) {
			const p = this.points_[i]
			if (p.x > xmax) xmax = p.x
			if (p.x < xmin) xmin = p.x
			if (p.y > ymax) ymax = p.y
			if (p.y < ymin) ymin = p.y
		}

		this.pmin_ = new Point(xmin, ymin)
		this.pmax_ = new Point(xmax, ymax)

		const dx = kAlpha * (xmax - xmin)
		const dy = kAlpha * (ymax - ymin)
		this.head_ = new Point(xmax + dx, ymin - dy)
		this.tail_ = new Point(xmin - dx, ymin - dy)

		// Sort points along y-axis
		this.points_.sort(Point.compare)
	}

	initEdges(polyline: XY[]): void {
		for (let i = 0; i < polyline.length; ++i) {
			this.edge_list.push(new Edge(polyline[i], polyline[(i + 1) % polyline.length]))
		}
	}

	getPoint(index: number): XY {
		return this.points_[index]
	}

	addToMap(triangle: Triangle): void {
		this.map_.push(triangle)
	}

	locateNode(point: XY): Node | null {
		return this.front_!.locateNode(point.x)
	}

	createAdvancingFront(): void {
		// Initial triangle
		const triangle = new Triangle(this.points_[0], this.tail_!, this.head_!)

		this.map_.push(triangle)

		const head = new Node(triangle.getPoint(1), triangle)
		const middle = new Node(triangle.getPoint(0), triangle)
		const tail = new Node(triangle.getPoint(2))

		this.front_ = new AdvancingFront(head, tail)

		head.next = middle
		middle.next = tail
		middle.prev = head
		tail.prev = middle
	}

	removeNode(_node: Node): void {
		// do nothing
	}

	mapTriangleToNodes(t: Triangle): void {
		for (let i = 0; i < 3; ++i) {
			if (!t.getNeighbor(i)) {
				const n = this.front_!.locatePoint(t.pointCW(t.getPoint(i))!)
				if (n) {
					n.triangle = t
				}
			}
		}
	}

	removeFromMap(triangle: Triangle): void {
		const map = this.map_
		for (let i = 0; i < map.length; i++) {
			if (map[i] === triangle) {
				map.splice(i, 1)
				break
			}
		}
	}

	/**
	 * Do a depth first traversal to collect triangles
	 */
	meshClean(triangle: Triangle): void {
		// New implementation avoids recursive calls and use a loop instead.
		// Cf. issues # 57, 65 and 69.
		const triangles: Triangle[] = [triangle]
		let t: Triangle | undefined

		while (t = triangles.pop()) {
			if (!t.isInterior()) {
				t.setInterior(true)
				this.triangles_.push(t)
				for (let i = 0; i < 3; i++) {
					if (!t.constrained_edge[i]) {
						const neighbor = t.getNeighbor(i)
						if (neighbor) {
							triangles.push(neighbor)
						}
					}
				}
			}
		}
	}
}

